import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Camera, Loader2, X } from 'lucide-react';
import { compressImage, uploadAttachment, uploadFailureKey } from '../../lib/image';

/**
 * Which end of the job this sheet is closing out.
 *
 * Copy only. The stage recorded against the photo is decided by the server from the
 * transition itself — COMPLETION on RESOLVED, VERIFICATION on VERIFIED — so the client
 * neither sends nor could forge it.
 */
export type ProofStage = 'resolve' | 'verify';

const FILENAME: Record<ProofStage, string> = {
  resolve: 'resolution.webp',
  verify: 'verification.webp',
};

interface ProofSheetProps {
  stage: ProofStage;
  onCancel: () => void;
  /** Runs the transition. `proofAttachmentId` is undefined when no photo was taken. */
  onConfirm: (proofAttachmentId?: string) => Promise<void>;
}

/**
 * Close-out capture, with an optional photo (decisions D33 for completion, R7 for
 * verification).
 *
 * The photo is offered here rather than demanded, and the wording says so — the confirm
 * button is enabled from the moment the sheet opens and never waits on an upload. D2
 * settled that proof is a quality metric (§5.6), not a gate: a technician in a basement
 * with a dead camera, a cracked lens or no light still has to be able to close the job,
 * and a blocking control would only teach people to photograph the floor. The same
 * reasoning applies unchanged to the manager signing it off a shift later.
 *
 * One component for both ends rather than two near-identical files: the only differences
 * are the copy and the filename, and a fork is how one of the two quietly acquires a gate.
 *
 * Capture happens here, but the upload is claimed by the transition afterwards — the
 * photo goes up orphaned first, so a slow link delays the proof rather than the close.
 */
export function ProofSheet({ stage, onCancel, onConfirm }: ProofSheetProps) {
  const { t } = useTranslation();
  const fileInput = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const previewUrl = useRef<string | null>(null);
  const [attachmentId, setAttachmentId] = useState<string | undefined>();
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Swaps the preview and frees the blob the old one pinned.
   *
   * `URL.createObjectURL` holds its blob alive until the URL is revoked, so every retake
   * used to leak a whole compressed photo for the life of the tab — on a handset with a
   * few hundred MB to spare, on a shift where a technician retakes a dark shot three or
   * four times. `CreateTicketForm` revokes on the same two occasions this now does:
   * replacement, and teardown below.
   */
  function replacePreview(url: string | null): void {
    if (previewUrl.current) URL.revokeObjectURL(previewUrl.current);
    previewUrl.current = url;
    setPreview(url);
  }

  useEffect(
    () => () => {
      if (previewUrl.current) URL.revokeObjectURL(previewUrl.current);
    },
    [],
  );

  async function capture(file: File): Promise<void> {
    setUploading(true);
    setError(null);
    try {
      // Compressed before it ever touches the network (CLAUDE.md): ≤1200px WebP, which
      // on hotel wifi is the difference between a close-out that lands and one that times
      // out mid-shift.
      const compressed = await compressImage(file);
      replacePreview(URL.createObjectURL(compressed.blob));
      setAttachmentId(await uploadAttachment(compressed.blob, FILENAME[stage]));
    } catch (caught) {
      // Recoverable and explicitly non-fatal: the sheet stays open, the photo is simply
      // not attached, and the job can still be closed without it. The cause is named —
      // too large, refused, signed out, no signal — because those want different actions.
      setError(`${t(uploadFailureKey(caught))} ${t(`${stage}.photoFailed`)}`);
      replacePreview(null);
      setAttachmentId(undefined);
    } finally {
      setUploading(false);
    }
  }

  async function confirm(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await onConfirm(attachmentId);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t(`${stage}.failed`));
      setBusy(false);
    }
  }

  // Portalled to the body, not rendered in place. The action toolbar is `position:
  // sticky`, which creates a stacking context unconditionally — z-index or not — so a
  // sheet rendered inside it is trapped beneath the fixed tab bar however high its own
  // z-index goes. Measured, not guessed: `elementFromPoint` over the confirm button
  // returned a tab-bar icon, and the tap never reached the button.
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end bg-black/70">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t(`${stage}.heading`)}
        className="w-full rounded-t-2xl border-t border-slate-800 bg-slate-950 p-4 pb-[max(1.5rem,var(--safe-bottom))] pl-[max(1rem,var(--safe-left))] pr-[max(1rem,var(--safe-right))]"
      >
        <header className="mb-3 flex items-center justify-between">
          <p className="text-base font-semibold text-slate-100">{t(`${stage}.heading`)}</p>
          <button
            type="button"
            onClick={onCancel}
            aria-label={t(`${stage}.cancel`)}
            className="flex size-12 min-h-0 items-center justify-center bg-transparent p-0 text-slate-400"
          >
            <X className="size-5" aria-hidden="true" />
          </button>
        </header>

        <p className="mb-3 text-sm text-slate-400">{t(`${stage}.photoOptional`)}</p>

        <input
          ref={fileInput}
          type="file"
          accept="image/*"
          // Opens the camera directly on Android rather than the gallery — the photo is
          // being taken now, standing in front of the thing that was fixed.
          capture="environment"
          className="hidden"
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) void capture(file);
          }}
        />

        {preview ? (
          <img
            src={preview}
            alt={t(`${stage}.photoAlt`)}
            className="mb-3 max-h-40 w-full rounded-xl object-cover"
          />
        ) : null}

        <button
          type="button"
          disabled={uploading || busy}
          onClick={() => fileInput.current?.click()}
          className="mb-2 flex min-h-[56px] w-full items-center justify-center gap-2 rounded-xl bg-slate-800 px-4 text-base font-semibold text-slate-100 disabled:bg-slate-900 disabled:text-slate-500"
        >
          {uploading ? (
            <Loader2 className="size-5 animate-spin" aria-hidden="true" />
          ) : (
            <Camera className="size-5" aria-hidden="true" />
          )}
          {preview ? t(`${stage}.retakePhoto`) : t(`${stage}.addPhoto`)}
        </button>

        {error && (
          <p role="alert" className="mb-2 text-sm text-red-400">
            {error}
          </p>
        )}

        <button
          type="button"
          // Never disabled by the absence of a photo — only by a request already running.
          // Not by an upload in flight either: a photo still climbing a 3G link must not
          // hold the close-out hostage.
          disabled={busy}
          onClick={() => void confirm()}
          className="flex min-h-[56px] w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 text-base font-semibold text-white disabled:bg-emerald-900 disabled:text-emerald-300"
        >
          {busy && <Loader2 className="size-5 animate-spin" aria-hidden="true" />}
          {t(`${stage}.confirm`)}
        </button>
      </div>
    </div>,
    document.body,
  );
}
